import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Activity, Filter, Loader2, RefreshCcw, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_BADGE = {
  'auth.admin_login': 'bg-[#EFF6FF] text-[#1D4ED8]',
  'user.role_changed': 'bg-amber-100 text-amber-800',
  'user.activated': 'bg-emerald-100 text-emerald-700',
  'user.deactivated': 'bg-red-100 text-red-700',
};

function formatWhen(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

export default function AdminActivity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', actor: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.actor) params.actor = filters.actor;
      const res = await axios.get(`${API_URL}/api/admin/activity`, { params, withCredentials: true });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout title="Activity Log">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap" data-testid="admin-activity-header">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><Activity size={22} /> Activity Log</h1>
          <p className="text-sm text-[#475569] mt-1">Tamper-evident audit trail of admin sign-ins and sensitive actions.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <form
            onSubmit={(e) => { e.preventDefault(); load(); }}
            className="flex items-end gap-3"
            data-testid="admin-activity-filter-form"
          >
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Action</label>
              <Input
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                placeholder="e.g. user.role_changed"
                className="w-56"
                data-testid="admin-activity-filter-action"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Actor email</label>
              <Input
                value={filters.actor}
                onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                placeholder="admin@example.com"
                className="w-64"
                data-testid="admin-activity-filter-actor"
              />
            </div>
            <Button type="submit" className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]" data-testid="admin-activity-apply-btn">
              <Filter size={14} className="mr-1.5" /> Apply
            </Button>
            <Button type="button" variant="outline" onClick={() => { setFilters({ action: '', actor: '' }); setTimeout(load, 0); }} data-testid="admin-activity-clear-btn">
              <RefreshCcw size={14} className="mr-1.5" /> Reset
            </Button>
          </form>
          <a
            href={`${API_URL}/api/admin/activity.csv${(filters.action || filters.actor) ? `?${new URLSearchParams({ ...(filters.action && { action: filters.action }), ...(filters.actor && { actor: filters.actor }) })}` : ''}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md"
            data-testid="admin-activity-export-csv"
          >
            <Download size={14} /> Export CSV
          </a>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        {/* Desktop / tablet: full table */}
        <div className="hidden md:block">
          <table className="w-full text-sm" data-testid="admin-activity-table">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8]">No activity yet.</td></tr>
              ) : items.map((it) => (
                <tr key={it.audit_id} className="hover:bg-[#F8FAFC]" data-testid={`admin-activity-row-${it.audit_id}`}>
                  <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{formatWhen(it.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0A0F1C]">{it.actor_name || '-'}</div>
                    <div className="text-xs text-[#94A3B8]">{it.actor_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={ACTION_BADGE[it.action] || 'bg-[#F1F5F9] text-[#475569]'} data-testid={`admin-activity-action-${it.audit_id}`}>{it.action}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#475569]">
                    {it.target_type ? <>{it.target_type} · <span className="font-mono">{it.target_id?.slice(0, 18)}</span></> : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#0A0F1C]">{it.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: stacked cards */}
        <div className="md:hidden divide-y divide-[#F1F5F9]" data-testid="admin-activity-mobile-list">
          {loading ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">No activity yet.</div>
          ) : items.map((it) => (
            <div key={it.audit_id} className="p-4 space-y-1.5" data-testid={`admin-activity-card-${it.audit_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0A0F1C] truncate">{it.actor_name || it.actor_email || '-'}</div>
                  <div className="text-[11px] text-[#94A3B8] truncate">{it.actor_email}</div>
                </div>
                <Badge className={ACTION_BADGE[it.action] || 'bg-[#F1F5F9] text-[#475569]'}>{it.action}</Badge>
              </div>
              {it.details && <div className="text-sm text-[#0A0F1C]">{it.details}</div>}
              <div className="flex items-center justify-between text-[11px] text-[#475569] pt-1">
                <span>{formatWhen(it.created_at)}</span>
                {it.target_type && <span className="font-mono text-[#94A3B8]">{it.target_type} · {it.target_id?.slice(0, 12)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
